import { useState, type ReactNode } from "react";
import { Navigate } from "react-router";
import { useMe } from "@/shared/lib/auth";
import { AuthLoadingOverlay } from "@/shared/components/ui/AuthLoadingOverlay";

interface RequireAdminProps {
  children: ReactNode;
}

/**
 * Gates the /app/admin/* subtree. Sits inside RequireAuth, so a session is
 * already guaranteed — this only adds the admin check. Non-admins are bounced
 * to the contacts home (the frontend is convenience; the API enforces the real
 * boundary via requireAdmin on every /admin route).
 *
 * Shares RequireAuth's loading treatment: the pulsing-logo overlay (rendered
 * outside the branch so it can fade out over the content). Because the session
 * is usually already cached here, the overlay typically no-ops immediately.
 */
export function RequireAdmin({ children }: RequireAdminProps) {
  const me = useMe();
  const [overlayGone, setOverlayGone] = useState(false);
  const loading = me.isLoading;

  if (!loading && !me.data) {
    return <Navigate to="/auth/login" replace />;
  }

  if (!loading && me.data && !me.data.isAdmin) {
    return <Navigate to="/app/contacts" replace />;
  }

  return (
    <>
      {!loading && me.data?.isAdmin ? children : null}
      {!overlayGone ? (
        <AuthLoadingOverlay show={loading} onExited={() => setOverlayGone(true)} />
      ) : null}
    </>
  );
}
